import { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLang } from "@/contexts/LanguageContext";
import { NAV } from "@/constants/testIds";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_intl-food-commerce/artifacts/zs0n14da_LOGO%20FOOD.png";

const Header = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about, testId: NAV.linkAbout },
    { id: "services", label: t.nav.services, testId: NAV.linkServices },
    { id: "products", label: t.nav.products, testId: NAV.linkProducts },
    { id: "markets", label: t.nav.markets, testId: NAV.linkMarkets },
    { id: "news", label: t.nav.news, testId: "nav-link-news", route: "/news" },
    { id: "faq", label: t.nav.faq, testId: NAV.linkFaq },
    { id: "contact", label: t.nav.contact, testId: NAV.linkContact },
  ];

  const handleLinkClick = (link) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (link.route) {
      navigate(link.route);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (!isHome) {
      navigate("/", { state: { scrollTo: link.id } });
      // schedule scroll after navigation
      setTimeout(() => {
        const el = document.getElementById(link.id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return;
    }
    const el = document.getElementById(link.id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleQuoteClick = (e) => {
    e.preventDefault();
    setOpen(false);
    if (!isHome) {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById("quote");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return;
    }
    const el = document.getElementById("quote");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 bg-[#FAFAF8]/90 backdrop-blur-xl ${
        scrolled ? "border-b border-[#E2E2DB] shadow-sm" : "border-b border-transparent"
      }`}
    >
      <div className="container-cust flex items-center justify-between h-20">
        {/* Logo */}
        <a
          href="/"
          data-testid={NAV.logo}
          onClick={(e) => {
            e.preventDefault();
            if (!isHome) {
              navigate("/");
            }
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3"
        >
          <img
            src={LOGO_URL}
            alt="Food & Beverage Services S.R.L."
            className="h-12 w-12 object-contain logo-blend"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif-display text-[15px] text-[#0E5A4E] font-medium">
              Food &amp; Beverage
            </span>
            <span className="text-[10px] tracking-[0.25em] uppercase text-[#4A5D58]">
              Services S.R.L.
            </span>
          </div>
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.id}
              data-testid={l.testId}
              href={l.route || `#${l.id}`}
              onClick={handleLinkClick(l)}
              className="text-sm text-[#1A2421] hover:text-[#0E5A4E] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="hidden lg:flex items-center gap-4">
          <div
            data-testid={NAV.langSwitcher}
            className="flex items-center gap-1 text-xs border border-[#E2E2DB] rounded-full px-1 py-1"
          >
            <Globe className="w-3.5 h-3.5 ml-2 text-[#4A5D58]" />
            <button
              data-testid={NAV.langItButton}
              onClick={() => setLang("it")}
              className={`px-2 py-1 rounded-full transition-colors ${
                lang === "it"
                  ? "bg-[#0E5A4E] text-white"
                  : "text-[#4A5D58] hover:text-[#0E5A4E]"
              }`}
            >
              IT
            </button>
            <button
              data-testid={NAV.langEnButton}
              onClick={() => setLang("en")}
              className={`px-2 py-1 rounded-full transition-colors ${
                lang === "en"
                  ? "bg-[#0E5A4E] text-white"
                  : "text-[#4A5D58] hover:text-[#0E5A4E]"
              }`}
            >
              EN
            </button>
          </div>
          <Button
            data-testid={NAV.ctaQuote}
            onClick={handleQuoteClick}
            className="bg-[#0E5A4E] hover:bg-[#0B4A40] text-white rounded-full px-5"
          >
            {t.nav.requestQuote}
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid={NAV.mobileToggle}
          className="lg:hidden p-2"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid={NAV.mobileMenu}
          className="lg:hidden border-t border-[#E2E2DB] bg-[#FAFAF8]/95 backdrop-blur-xl"
        >
          <div className="container-cust py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.route || `#${l.id}`}
                onClick={handleLinkClick(l)}
                className="text-base text-[#1A2421] hover:text-[#0E5A4E]"
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setLang("it")}
                className={`px-3 py-1 rounded-full text-xs ${
                  lang === "it"
                    ? "bg-[#0E5A4E] text-white"
                    : "border border-[#E2E2DB] text-[#4A5D58]"
                }`}
              >
                IT
              </button>
              <button
                onClick={() => setLang("en")}
                className={`px-3 py-1 rounded-full text-xs ${
                  lang === "en"
                    ? "bg-[#0E5A4E] text-white"
                    : "border border-[#E2E2DB] text-[#4A5D58]"
                }`}
              >
                EN
              </button>
            </div>
            <Button
              onClick={handleQuoteClick}
              className="bg-[#0E5A4E] hover:bg-[#0B4A40] text-white rounded-full w-full"
            >
              {t.nav.requestQuote}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
